import { useContext } from 'react';
import type {
  ColDef,
  ColumnMenuTab,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community';
import timeStampToDate from '@/helpers/timeStampToDate';
import numberHumanizer from '@/helpers/numberHumanizer';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import {
  dateFilterGetter,
  decodeGetter,
  valueFormatter
} from '@/app/common/components/ag-grid/configs';
import { Link } from 'react-router-dom';
import { commonTooltips } from '@/app/common/tooltip/commonTooltips';
import { getColor } from '@/helpers/utils';
import { RemovePlannerContext } from '@/context/Context';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GscStatusRenderer, {
  getGscStatusConfig
} from '@/app/common/components/CellRenderers/GscStatusRenderer';
import type { PlannerHistoryItem } from '@/app/KeywordResearch/types/googleAdsApiTypes';
import ClusterFromTaskMenuItem from '@/app/KeywordResearch/components/ClusterFromTaskMenuItem';
import { toUrlKeywordSegment } from '@/app/KeywordResearch/utils/toUrlKeywordSegment';

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

type GeoLocationItem = {
  code: string;
  fa_name: string;
};

type GeoLanguageItem = {
  code: string;
  fa_name: string;
};

type PlannerHistoryColumn = ColDef<PlannerHistoryItem> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
};

type RemovePlannerContextValue = {
  setTaskId: (taskId: string) => void;
  setShow: (show: boolean) => void;
  setKeyword: (keyword: string) => void;
};

const GLOBAL_FLAG_URL = 'https://static.mizfa.tools/flags/global.webp';

/** API may return null/undefined/"" for worldwide / all languages */
const isAllGeoCode = (code: string | null | undefined): boolean =>
  code == null || code === '';

const toUrlGeoSlug = (code: string | null | undefined): string =>
  isAllGeoCode(code) ? 'all' : code.toLowerCase();

export const plannerHistoryColumns = (
  geoLocationsData: GeoLocationItem[],
  geoLanguagesData: GeoLanguageItem[]
): PlannerHistoryColumn[] => [
  {
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    field: 'keyword',
    type: 'string',
    title: 'کلمه یا آدرس',
    flex: 1,
    minWidth: 250,
    searchable: true,
    cellRenderer: (params: ICellRendererParams<PlannerHistoryItem>) => (
      <Link
        to={`${params.data?.task_id}/${toUrlGeoSlug(params.data?.location_code)}/${toUrlGeoSlug(params.data?.language_code)}/${toUrlKeywordSegment(params.data?.keyword)}`}
        style={{
          color: getColor('navbar-vertical-default-link-active-color')
        }}
        className={`fw-bolder btn-link ${!params.data?.task_id && 'disabled-link'}`}
      >
        <div className="d-flex gap-1 align-items-baseline">
          {valueFormatter(params as ValueFormatterParams)}
        </div>
      </Link>
    )
  },
  {
    searchable: true,
    menuTabs,
    floatingFilter: false,
    filterValueGetter: decodeGetter,
    field: 'location_code',
    type: 'string',
    title: 'مکان جستجو',
    width: 125,
    cellRenderer: (params: ICellRendererParams<PlannerHistoryItem>) => {
      const isWorldwide = isAllGeoCode(params.value);
      return (
        <div className="d-flex gap-2 align-items-center">
          <img
            src={
              isWorldwide
                ? GLOBAL_FLAG_URL
                : `https://static.mizfa.tools/flags/${params.value}.webp`
            }
            alt=""
          />
          {isWorldwide
            ? 'کل جهان'
            : geoLocationsData.find(item => item.code === params.value)
                ?.fa_name}
        </div>
      );
    }
  },
  {
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: false,
    field: 'language_code',
    type: 'string',
    title: 'زبان جستجو',
    width: 125,
    cellRenderer: (params: ICellRendererParams<PlannerHistoryItem>) =>
      isAllGeoCode(params.value)
        ? 'همه زبان ها'
        : geoLanguagesData.find(item => item.code === params.value)?.fa_name
  },
  {
    field: 'num_of_keywords',
    type: 'number',
    title: 'تعداد کلمات',
    width: 120,
    searchable: true,
    menuTabs,
    floatingFilter: false
  },
  {
    field: 'sum_of_search_volumes',
    type: 'number',
    title: 'میزان جستجوی کل',
    width: 150,
    searchable: true,
    menuTabs,
    floatingFilter: false,
    cellRenderer: (params: ICellRendererParams<PlannerHistoryItem>) => (
      <>
        {!params.value ? (
          <HelpTooltip html={commonTooltips.searchVolume} />
        ) : (
          numberHumanizer(params.value)
        )}
      </>
    )
  },
  {
    field: 'status',
    type: 'string',
    title: 'وضعیت',
    width: 130,
    searchable: true,
    menuTabs,
    floatingFilter: false,
    filterValueGetter: (params: ValueGetterParams<PlannerHistoryItem>) =>
      getGscStatusConfig(params.data?.status).label,
    cellRenderer: (params: ICellRendererParams<PlannerHistoryItem>) => (
      <GscStatusRenderer status={params.value} />
    )
  },
  {
    field: 'inserted_at_timestamp',
    type: 'string',
    title: 'تاریخ درج',
    menuTabs,
    floatingFilter: false,
    filterValueGetter: dateFilterGetter,
    width: 150,
    noFilter: true,
    searchable: true,
    valueFormatter: params =>
      timeStampToDate(Number(params.value)).slice(8).toPersianDigit()
  },
  {
    field: 'url',
    type: 'array',
    title: ' ',
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 70,
    pinned: 'left',
    noFilter: true,
    cellRenderer: (params: ICellRendererParams<PlannerHistoryItem>) => {
      const {
        setTaskId: setRemTaskId,
        setShow,
        setKeyword: setRemKeyword
      } = useContext(RemovePlannerContext) as RemovePlannerContextValue;

      return (
        <span className="allaho_akbar_zIndex">
          <Dropdown
            onClick={e => e.stopPropagation()}
            align="end"
            className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <ClusterFromTaskMenuItem
                source="planner"
                taskId={params.data?.task_id}
                keywordCount={params.data?.num_of_keywords}
                isTaskReady={params.data?.status === 'finished'}
              />
              <Dropdown.Divider className="my-0" />
              <Dropdown.Item
                onClick={() => {
                  setRemKeyword(params.data?.keyword ?? '');
                  setRemTaskId(params.data?.task_id ?? '');
                  setShow(true);
                }}
                className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
                حذف
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </span>
      );
    }
  }
];
